import { Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo } from "react";

import PaginationControlsV2 from "../../../../../global/PaginationControlsV2";
import HNSColors from "../../../../../ui library/HNSColors";
import CheckBox from "../../../provider-management/components/fields/CheckBox";
import LoadingIndicator from "../../../provider-management/components/fields/LoadingIndicator";
import SearchBar from "../../../provider-management/components/fields/SearchBar";
import ServicesHeader from "../../../provider-management/components/services-provided/ServicesHeader";
import STRINGS from "../../../provider-management/utils/strings";

import ServiceRow from "./ServiceRow";
import { AVAILABLE_LIMIT, getServiceId, PROVIDED_LIMIT } from "./servicesUtils";

export default function ServicesPanel({
  currentPage,
  data,
  loading,
  onPageChange,
  onSearchChange,
  onSelectAll,
  onToggleRow,
  readOnly,
  resetCurrentPage,
  resultCount,
  searchText,
  selectedServices,
  showAddedState,
  title,
}) {
  const selectedIds = useMemo(() => new Set(selectedServices.map((item) => getServiceId(item))), [selectedServices]);
  const selectableItems = useMemo(
    () => (showAddedState ? data.filter((item) => item.serviceType !== STRINGS.SERVICE_TYPE.AVAILABLE) : data),
    [data, showAddedState]
  );
  const areAllSelectableItemsChecked =
    selectableItems.length > 0 && selectableItems.every((item) => selectedIds.has(getServiceId(item)));
  const resultLimit = title === STRINGS.HEADERS.AVAILABLE_SERVICES ? AVAILABLE_LIMIT : PROVIDED_LIMIT;

  return (
    <div className="flex h-[420px] min-h-0 flex-1 flex-col gap-2 rounded-[14px] p-4" style={{ backgroundColor: title === STRINGS.HEADERS.AVAILABLE_SERVICES ? HNSColors.tertiary[975] : HNSColors.neutrals[900] }}>
      {loading ? <LoadingIndicator /> : <>
        <ServicesHeader title={title} selectedServices={selectedServices.length} totalServices={resultCount} />
        <SearchBar searchText={searchText} setSearchText={onSearchChange} resetCurrentPage={resetCurrentPage} />
        {data.length ? (
          <div className="flex h-full min-h-0 flex-col gap-4">
            <CheckBox checked={areAllSelectableItemsChecked} handleClick={onSelectAll} readOnly={readOnly} label={<Typography sx={{ color: readOnly ? HNSColors.neutrals[500] : HNSColors.neutrals[400] }}>{STRINGS.HEADERS.SELECT_ALL}</Typography>} />
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto [&::-webkit-scrollbar-thumb]:bg-blue-700 [&::-webkit-scrollbar]:[width:6px]">
              <div className="flex flex-col gap-4 px-2">
                {data.map((item) => <ServiceRow key={getServiceId(item)} checked={selectedIds.has(getServiceId(item))} data={item} showAddedTag={showAddedState && item.serviceType === STRINGS.SERVICE_TYPE.AVAILABLE} onToggle={onToggleRow} readOnly={readOnly} />)}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center"><Typography sx={{ color: HNSColors.neutrals[500] }}>Nothing to show</Typography></div>
        )}
        {resultCount > resultLimit ? (
          <div className="flex w-full justify-center"><Stack alignItems="center"><PaginationControlsV2 alignment="center" resultCount={resultCount} handleChange={onPageChange} currentPage={currentPage} resultLimit={resultLimit} /></Stack></div>
        ) : null}
      </>}
    </div>
  );
}

ServicesPanel.propTypes = {
  currentPage: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onToggleRow: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  resetCurrentPage: PropTypes.func.isRequired,
  resultCount: PropTypes.number.isRequired,
  searchText: PropTypes.string.isRequired,
  selectedServices: PropTypes.array.isRequired,
  showAddedState: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

ServicesPanel.defaultProps = {
  readOnly: false,
  showAddedState: false,
};
